import { THEME } from '@Theme';
import { StyleSheet } from 'aphrodite';

const {
	colors,
	common: { borderRadius },
	spacing,
	typography,
} = THEME;

const ICON_TILE_SIZE = 44;
const CARET_SIZE = 16;
const ROW_GAP = spacing.sm + spacing.xs;
const ROW_INDENT = spacing.md + CARET_SIZE + ROW_GAP;

const STATUS_COLUMN_WIDTH = 200;
const DATE_COLUMN_WIDTH = 120;
const ACTION_COLUMN_WIDTH = 160;

export const styleSheet = StyleSheet.create({
	card: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.md,
		padding: spacing.lg,
		width: '100%',
	},

	ghost: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.md,
		padding: spacing.lg,
		width: '100%',
	},

	header: {
		alignItems: 'flex-start',
		display: 'flex',
		gap: spacing.md,
	},

	iconTile: {
		alignItems: 'center',
		backgroundColor: colors.warning.main,
		borderRadius,
		color: colors.primary.contrastText,
		display: 'flex',
		flex: 'none',
		height: ICON_TILE_SIZE,
		justifyContent: 'center',
		width: ICON_TILE_SIZE,
	},

	headerGroup: {
		display: 'flex',
		flex: 1,
		flexDirection: 'column',
		gap: spacing.xs,
	},

	headerLabelRow: {
		alignItems: 'center',
		display: 'flex',
		gap: spacing.xs,
	},

	headerContent: {
		color: colors.text.light,
		fontSize: typography.fontSizes.md,
		margin: 0,
	},

	headerHelp: {
		alignItems: 'center',
		display: 'flex',
	},

	headerHelpText: {
		fontSize: typography.fontSizes.sm,
	},

	count: {
		color: colors.primary.main,
		// Off the type scale to match the stat on the other dashboard cards.
		fontSize: '1.625rem',
		fontWeight: typography.fontWeights.bold,
	},

	description: {
		color: colors.text.light,
		fontSize: typography.fontSizes.sm,
		margin: 0,
	},

	toggleAll: {
		flex: 'none',
	},

	divider: {
		backgroundColor: colors.border.light,
		height: 1,
	},

	viewAllRow: {
		alignItems: 'center',
		display: 'flex',
		gap: spacing.md,
	},

	showingCount: {
		color: colors.text.light,
		fontSize: typography.fontSizes.sm,
	},

	list: {
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
	},

	item: {
		border: `1px solid ${colors.border.light}`,
		borderRadius,
		overflow: 'hidden',
	},

	itemHeader: {
		alignItems: 'center',
		display: 'flex',
		gap: ROW_GAP,
		paddingRight: spacing.md,
	},

	itemToggle: {
		alignItems: 'center',
		background: 'none',
		border: 'none',
		cursor: 'pointer',
		display: 'flex',
		flex: 1,
		gap: ROW_GAP,
		minWidth: 0,
		padding: `${spacing.sm}px ${spacing.md}px`,
		textAlign: 'left',
		// Without this a nowrap title sets the dashboard column's minimum width and scrolls the page sideways.
		width: 0,
		':hover': {
			backgroundColor: colors.background.main,
		},
	},

	caret: {
		color: colors.icon.gray,
		flex: 'none',
		transition: 'transform 150ms ease',
		'@media (prefers-reduced-motion: reduce)': {
			transition: 'none',
		},
	},

	caretOpen: {
		transform: 'rotate(90deg)',
	},

	itemTitle: {
		color: colors.text.main,
		flex: 1,
		fontSize: typography.fontSizes.md,
		fontWeight: typography.fontWeights.bold,
		margin: 0,
		minWidth: 0,
		...THEME.common.truncate,
	},

	statusColumn: {
		display: 'flex',
		flex: 'none',
		width: STATUS_COLUMN_WIDTH,
	},

	dateColumn: {
		color: colors.text.light,
		display: 'flex',
		flex: 'none',
		flexDirection: 'column',
		fontSize: typography.fontSizes.xs,
		fontWeight: typography.fontWeights.semibold,
		textAlign: 'center',
		width: DATE_COLUMN_WIDTH,
	},

	overdue: {
		color: colors.danger.main,
	},

	actionColumn: {
		display: 'flex',
		flex: 'none',
		width: ACTION_COLUMN_WIDTH,
	},

	itemAction: {
		whiteSpace: 'nowrap',
		width: '100%',
	},

	itemBody: {
		borderTop: `1px solid ${colors.border.light}`,
		display: 'flex',
		flexDirection: 'column',
		gap: spacing.sm,
		padding: `${ROW_GAP}px ${spacing.md}px ${spacing.md}px ${ROW_INDENT}px`,
	},

	itemDetail: {
		color: colors.text.light,
		fontSize: typography.fontSizes.sm,
	},

	itemNote: {
		backgroundColor: colors.background.main,
		borderRadius,
		color: colors.text.main,
		fontSize: typography.fontSizes.sm,
		padding: `${spacing.sm}px ${ROW_GAP}px`,
	},

	itemTracking: {
		color: colors.text.light,
		fontSize: typography.fontSizes.xs,
	},
});
